import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { fromLonLat } from "ol/proj";
import {
  createTrackingCluster,
  getVisibleClusters,
  getMapViewport,
  isCluster,
  getClusterExpansionZoom,
} from "./clusterUtils";
import { clearMapClusters } from "./clusterRenderers";

/**
 * React hook for map marker clustering on the tracking overview pages.
 *
 * Works with all 3 map providers (QIMap/OpenLayers, Mapbox, Google Maps).
 * Returns a stable clusterIndex and a `zoomVersion` counter that increments
 * on every zoom/pan, forcing the consuming useEffect to re-run and
 * recompute clusters synchronously.
 *
 * @param {Object} params
 * @param {Object} params.mapRef - Map reference (QIMap, Mapbox, or Google instance)
 * @param {string} params.mapProvider - "qiMap", "mapbox", or "google"
 * @param {Array} params.trackingPoints - Raw tracking points array
 * @param {boolean} params.enabled - Whether clustering is active (false when a device/vehicle is selected)
 * @param {Object} params.clusterOptions - Optional supercluster config overrides
 * @returns {Object} - { clusterIndex, zoomVersion, isCluster, onClusterClick, computeClusters }
 */
export function useMapClustering({
  mapRef,
  mapProvider,
  trackingPoints,
  enabled = true,
  clusterOptions,
}) {
  // zoomVersion increments every time the map moves, triggering re-render
  const [zoomVersion, setZoomVersion] = useState(0);
  const debounceTimerRef = useRef(null);
  const clusterOptionsRef = useRef(clusterOptions);
  clusterOptionsRef.current = clusterOptions;

  // Build supercluster index when tracking points change
  const clusterIndex = useMemo(() => {
    if (!enabled || !trackingPoints || trackingPoints.length === 0) {
      return null;
    }
    return createTrackingCluster(trackingPoints, clusterOptionsRef.current);
  }, [trackingPoints, enabled]);

  // Provider renderers retain their own layers/overlays. Remove those artifacts
  // when selection disables clustering or the current results have no cluster index.
  useEffect(() => {
    if (!enabled || !clusterIndex) {
      clearMapClusters(mapRef, mapProvider);
    }
  }, [enabled, clusterIndex, mapRef, mapProvider]);

  // Compute clusters synchronously given current map state
  const computeClusters = useCallback(() => {
    if (!clusterIndex || !mapRef || !enabled) {
      return [];
    }
    const viewport = getMapViewport(mapRef, mapProvider);
    if (!viewport) {
      return [];
    }
    return getVisibleClusters(clusterIndex, viewport.bbox, viewport.zoom);
  }, [clusterIndex, mapRef, mapProvider, enabled]);

  // Subscribe to map zoom/pan events — increment zoomVersion to trigger re-render
  useEffect(() => {
    if (!mapRef || !enabled || !clusterIndex) return;

    let cleanup = () => {};

    const bumpVersion = () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        setZoomVersion((v) => v + 1);
      }, 150);
    };

    if (!mapProvider || mapProvider === "qiMap") {
      if (mapRef.getMapReference) {
        const olMap = mapRef.getMapReference();
        if (olMap && olMap.on) {
          olMap.on("moveend", bumpVersion);
          cleanup = () => olMap.un("moveend", bumpVersion);
        }
      }
    } else if (mapProvider === "mapbox") {
      if (mapRef.on) {
        mapRef.on("moveend", bumpVersion);
        cleanup = () => mapRef.off("moveend", bumpVersion);
      }
    } else if (mapProvider === "google") {
      if (mapRef.addListener) {
        const listener = mapRef.addListener("idle", bumpVersion);
        cleanup = () => google.maps.event.removeListener(listener);
      }
    }

    return () => {
      cleanup();
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [mapRef, mapProvider, enabled, clusterIndex]);

  // Handler for clicking on a cluster — zooms to expansion level
  const onClusterClick = useCallback(
    (feature) => {
      if (!isCluster(feature) || !clusterIndex || !mapRef) return;

      const clusterId = feature.properties.cluster_id;
      const expansionZoom = getClusterExpansionZoom(clusterIndex, clusterId);
      const [lng, lat] = feature.geometry.coordinates;

      if (!mapProvider || mapProvider === "qiMap") {
        if (mapRef.getMapReference) {
          const olMap = mapRef.getMapReference();
          if (olMap) {
            const view = olMap.getView();
            window.qiMapProgrammaticChange = true;
            view.animate(
              {
                center: fromLonLat([lng, lat]),
                zoom: expansionZoom,
                duration: 300,
              },
              () => {
                window.qiMapProgrammaticChange = false;
              }
            );
          }
        }
      } else if (mapProvider === "mapbox") {
        mapRef.easeTo({
          center: [lng, lat],
          zoom: expansionZoom,
          duration: 300,
        });
      } else if (mapProvider === "google") {
        mapRef.panTo({ lat, lng });
        mapRef.setZoom(expansionZoom);
      }
    },
    [mapRef, mapProvider, clusterIndex]
  );

  return {
    clusterIndex,
    zoomVersion,
    isCluster,
    onClusterClick,
    computeClusters,
  };
}
